import React, { useEffect, useRef } from 'react';
import { useTypedSelector } from 'state';
import { SettingsTitle } from '../../../preferences/components/SettingsTitle';
import { SettingsDescription } from '../../../preferences/components/SettingsDescription';
import { Toggle, ToggleApi } from '../../../common/toggle';
import { Tooltip } from '../../../moderation-v2/sharable/components/Tooltip';

import styles from './AIModerationAssistSetting.module.scss';
import Icon from 'utils/components/icon';
import classNames from 'classnames';
import { LearnMore } from '../../../preferences/components/LearnMore';
import { APPROVE_ALL } from '../moderation-constants';
import { openModal } from '../../../moderation-v2/comments-and-posts/modals/state/actions';
import { useDispatch } from 'react-redux';
import { ModalType } from '../../../moderation-v2/comments-and-posts/modals';
import { ConfirmationModalProps } from '../../../moderation-v2/sharable/components/modals/ConfirmationModal';
import { createTwoButtonsRenderActions } from '../../../moderation-v2/sharable/components/modals/ConfirmationModal/confirmationModalUtils';

export interface AIModerationAssistSettingProps {
  isEnabled: boolean;
  onChange: (isEnabled: boolean) => void;
  moderationDefaultPolicy: string;
}

export function AIModerationAssistSetting(props: AIModerationAssistSettingProps) {
  const { isEnabled, onChange, moderationDefaultPolicy } = props;
  const models = useTypedSelector(state => state.settings.policy.moderationFilters.models);
  const hasModels = Object.values(models).some(model => model.value !== null);
  const [isInternalEnabled, setIsInternalEnabled] = React.useState(isEnabled);
  const toggleApi = useRef<ToggleApi>(null);

  const disabledStyle = { [styles.disabled]: !hasModels };

  const dispatch = useDispatch();

  useEffect(() => {
    setIsInternalEnabled(isEnabled);
  }, [isEnabled]);

  const onMaybeChange = (value: boolean) => {
    if (value && moderationDefaultPolicy !== APPROVE_ALL) {
      const modalProps: ConfirmationModalProps = {
        title: 'Confirm Action',
        description:
          'Enabling AI Moderation Assist allows the assistant to review your pending comments',
        renderActions: createTwoButtonsRenderActions({
          primaryButtonOnClick: () => {
            onChange(value);
          },
          secondaryButtonOnClick: () => {
            toggleApi.current?.off();
          },
          primaryButtonLabel: 'Confirm'
        })
      };

      dispatch(openModal({ modalType: ModalType.CONFIRM_GENERAL, modalProps }));
    } else {
      onChange(value);
    }
  };

  return (
    <>
      <SettingsTitle
        title="AI Moderation Assist"
        titleClassName={classNames(disabledStyle)}
        afterTitle={
          hasModels ? null : (
            <Tooltip
              title="Turn on at least one automated moderation filter above to use this feature"
              className={styles.tooltip}
            >
              <div className={styles.iconWrapper}>
                <Icon name="circle-bolded-error" className={styles.infoIcon} />
              </div>
            </Tooltip>
          )
        }
      />
      <SettingsDescription
        description="Enable AI to swiftly clear pending comments queue by auto-rejecting inappropriate comments. It will significantly reduce manual moderation efforts and enhance moderation efficiency. "
        className={classNames(disabledStyle)}
        afterDescription={
          <LearnMore href="https://docs.google.com/document/d/1BM29aGm1ls74WQfw-CzTrVi4sribcM0FxZNvmDdmiyk/edit" />
        }
      />
      <Toggle
        checked={isInternalEnabled}
        onChange={onMaybeChange}
        disabled={!hasModels}
        apiRef={toggleApi}
      />
    </>
  );
}
